import { useMemo, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { getDateFnsLocale, getSundayFirstWeekdays } from "@/i18n/dateLocale";
import type { AvailabilityData } from "@/lib/api";

export interface SingleDateCalendarProps {
  value: string;
  onSelect: (date: string) => void;
  availability?: AvailabilityData | null;
  /** Years shown in the year jump list, counted from this year. */
  yearsAhead?: number;
  /** Event end picker: do not end before start or span an unavailable date. */
  rangeStart?: string;
}

const iso = (y: number, m: number, d: number) =>
  `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

/**
 * One-tap date picker for chauffeur / event bookings. The month-year header
 * opens a grid of months grouped by year, so a date 12+ months away is two
 * taps to the month plus one for the day; unavailable days are disabled, and the chosen day stays marked.
 */
export function SingleDateCalendar({ value, onSelect, availability, yearsAhead = 3, rangeStart }: SingleDateCalendarProps) {
  const { t, i18n } = useTranslation();
  const locale = getDateFnsLocale(i18n?.language ?? "en");
  const weekdays = useMemo(() => getSundayFirstWeekdays(i18n?.language ?? "en"), [i18n?.language]);
  const today = new Date();
  const [view, setView] = useState(() => {
    const seed = value ? new Date(`${value}T00:00:00`) : today;
    return Number.isNaN(seed.getTime())
      ? { year: today.getFullYear(), month: today.getMonth() }
      : { year: seed.getFullYear(), month: seed.getMonth() };
  });

  const [pickingMonth, setPickingMonth] = useState(false);
  const advance = availability?.window?.advance_notice_days || 0;
  const earliest = new Date(today.getFullYear(), today.getMonth(), today.getDate() + advance);
  const earliestIso = iso(earliest.getFullYear(), earliest.getMonth(), earliest.getDate());
  const availableTo = availability?.window?.available_to?.slice(0, 10) || null;
  const blocked = availability?.blockedDates ?? [];
  const isDisabled = (d: string) =>
    d < earliestIso || (!!availableTo && d > availableTo) ||
    (!!rangeStart && (d < rangeStart || blocked.some(b =>
      b.start_date.slice(0, 10) <= d && b.end_date.slice(0, 10) >= rangeStart))) ||
    blocked.some(b => d >= b.start_date.slice(0, 10) && d <= b.end_date.slice(0, 10));

  const minYear = today.getFullYear();
  const years = Array.from({ length: yearsAhead + 1 }, (_, i) => minYear + i);
  const atStart = view.year === minYear && view.month <= today.getMonth();
  const shift = (delta: number) => setView(v => {
    const m = v.month + delta;
    const y = v.year + Math.floor(m / 12);
    return { year: y, month: ((m % 12) + 12) % 12 };
  });

  const firstDay = new Date(view.year, view.month, 1).getDay();
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
  const todayIso = iso(today.getFullYear(), today.getMonth(), today.getDate());

  return (
    <div className="w-full select-none" data-testid="single-date-calendar">
      <div className="mb-2 flex items-center gap-1.5">
        <button type="button" onClick={() => shift(-1)} disabled={atStart}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-muted disabled:opacity-30"
          aria-label={t("dateRange.previous_month")} data-testid="single-date-prev">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => setPickingMonth(p => !p)}
          aria-expanded={pickingMonth}
          aria-label={t("booking.choose_month_year", "Choose month and year")}
          data-testid="single-date-month-year"
          className="flex min-h-10 flex-1 items-center justify-center gap-1 rounded-md px-2 text-base font-semibold capitalize hover:bg-muted md:text-sm"
        >
          {format(new Date(view.year, view.month, 1), "LLLL yyyy", { locale })}
          <ChevronDown className={cn("h-4 w-4 transition-transform", pickingMonth && "rotate-180")} aria-hidden="true" />
        </button>
        <button type="button" onClick={() => shift(1)} disabled={view.year >= years[years.length - 1] && view.month === 11}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-muted disabled:opacity-30"
          aria-label={t("dateRange.next_month")} data-testid="single-date-next">
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
      {pickingMonth ? (
        <div className="max-h-[17rem] space-y-3 overflow-y-auto pr-1" data-testid="single-date-month-grid">
          {years.map(y => (
            <div key={y}>
              <p className="mb-1 px-1 text-xs font-bold text-muted-foreground">{y}</p>
              <div className="grid grid-cols-4 gap-1">
                {Array.from({ length: 12 }, (_, m) => {
                  const past = y === minYear && m < today.getMonth();
                  const active = y === view.year && m === view.month;
                  return (
                    <button
                      key={m}
                      type="button"
                      disabled={past}
                      onClick={() => { setView({ year: y, month: m }); setPickingMonth(false); }}
                      data-testid={`single-date-month-${y}-${String(m + 1).padStart(2, "0")}`}
                      className={cn(
                        "min-h-10 rounded-md text-sm capitalize transition-colors",
                        past ? "cursor-not-allowed text-muted-foreground/40" : "hover:bg-muted",
                        active && "bg-foreground font-semibold text-background hover:bg-foreground",
                      )}
                    >
                      {format(new Date(y, m, 1), "LLL", { locale })}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
      <div className="grid grid-cols-7 justify-items-center gap-y-0.5">
        {weekdays.map(({ label, name }, i) => (
          <div key={i} className="flex h-7 w-10 items-center justify-center text-xs font-medium text-muted-foreground">
            <span aria-hidden="true">{label}</span><span className="sr-only">{name}</span>
          </div>
        ))}
        {Array.from({ length: firstDay }, (_, i) => <div key={`e${i}`} className="h-10 w-10" />)}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const d = iso(view.year, view.month, i + 1);
          const disabled = isDisabled(d);
          const selected = d === value;
          return (
            <button
              key={d}
              type="button"
              disabled={disabled}
              aria-pressed={selected}
              aria-label={d}
              data-testid={`single-date-day-${d}`}
              onClick={() => onSelect(d)}
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-full text-sm transition-colors",
                disabled && "cursor-not-allowed text-muted-foreground/40 line-through",
                !disabled && !selected && "hover:bg-muted",
                selected && "bg-foreground font-semibold text-background",
                d === todayIso && !selected && "font-semibold text-primary",
              )}
            >
              {i + 1}
            </button>
          );
        })}
      </div>
      )}
    </div>
  );
}

export default SingleDateCalendar;
